// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of information or reproduction of material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CustomCartLinesHeader from "@guestsupply/App/Components/Cart/CartLinesHeader";
import LinesHeader, { LinesHeaderStyles } from "@guestsupply/App/Components/Cart/LinesHeader";
import XngSticky from "@guestsupply/App/Components/XngSticky/XngSticky";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getFlatGroups, Group, SubGroups } from "@guestsupply/XDL/Utilities/getFlatGroups";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCartState,
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import CartLineCardExpandedComponent from "@insite/content-library/Components/CartLineCardExpanded";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import CartLineCardCondensed, {
    CartLineCardCondensedStyles,
} from "@insite/content-library/Widgets/Cart/CartLineCardCondensed";
import CartLineCardExpanded, {
    CartLineCardExpandedStyles,
} from "@insite/content-library/Widgets/Cart/CartLineCardExpanded";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showLineNotes = "showLineNotes",
    showImage = "showImage",
    showProductTitle = "showProductTitle",
    showBrand = "showBrand",
    showRemoveAllActions = "showRemoveAllActions",
    showPerUnitPrice = "showPerUnitPrice",
    showPriceLabel = "showPriceLabel",
    showAddToList = "showAddToList",
    showItemCount = "showItemCount",
    showProductNumber = "showProductNumber",
    showBackToTop = "showBackToTop",
    showVmiBinDetails = "showVmiBinDetails",
    customCSS = "customCSS",
    // XNG-Changes
    editable = "editable",
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const defaultCustomCss = `.main-grid-container{
}

.title-grid-item{
}

.title{
}

.description-grid-item{
}

.description-text{
}

.email-grid-item{
}

.email-text-field{
}

.email-button{
}

.disclaimer-grid-item{
}

.disclaimer-text{
}`;
interface OwnProps extends WidgetProps {
    fields: {
        [fields.showLineNotes]: boolean;
        [fields.showImage]: boolean;
        [fields.showProductTitle]: boolean;
        [fields.showBrand]: boolean;
        [fields.showRemoveAllActions]: boolean;
        [fields.showPerUnitPrice]: boolean;
        [fields.showPriceLabel]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showItemCount]: boolean;
        [fields.showProductNumber]: boolean;
        [fields.showBackToTop]: boolean;
        [fields.showVmiBinDetails]: boolean;
        [fields.customCSS]: string;
        // XNG-Chages
        [fields.editable]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.orderConfirmation;
    const isOrderConfirmationPage = getCurrentPage(state).type === "OrderConfirmationPage";
    const cartState = cartId && isOrderConfirmationPage ? getCartState(state, cartId) : getCurrentCartState(state);
    return {
        cart: cartState.value,
        promotionsDataView: getCurrentPromotionsDataView(state),
        settingsCollection: getSettingsCollection(state),
        isClearingCart: state.pages.cart.isClearingCart,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isLoading: state.data.carts.isLoading,
        isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
        showPricing: state.context.showPricing,
        showInventory: state.context.showInventory,
        // XNG-CHANGES
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
        isCartPage: getCurrentPage(state).type === "CartPage",
        isOrderConfirmationPage: getCurrentPage(state).type === "OrderConfirmationPage",
        isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
    };
};

const mapDispatchToProps = {
    clearCurrentCart,
    updateCartLine,
    removeCartLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export interface CartLinesStyles {
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noCartLinesText?: TypographyProps;
    noCartLinesIcon?: IconPresentationProps;
    headerWrapper?: InjectableCss;
    header?: CartLinesHeaderStyles;
    cardList?: CardListStyles;
    cardContainer?: CardContainerStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    footerWrapper?: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
    // XNG-CHANGES
    itemDescriptionWapper?: InjectableCss;
    groupsWrapper?: InjectableCss;
    groupWrapper?: InjectableCss;
    orderLinesTypography?: TypographyProps;
    tooltipStyle?: TooltipPresentationProps;
    freeShippingLink?: LinkPresentationProps;
    groupsLinesHeaderStyles?: LinesHeaderStyles;
    subGroupsLinesHeaderStyles?: LinesHeaderStyles;
    linesHeaderLabelTypography?: TypographyProps;
    shippingHandlingTypography?: TypographyProps;
    loadingSpinnerCenteringWrapper?: InjectableCss;
}
interface CartLinesHeaderStyles {
    itemCountText?: TypographyProps;
    condensedViewCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    condensedViewCheckbox?: CheckboxPresentationProps;
    overflowMenu?: OverflowMenuPresentationProps;
    removeAllLink?: LinkPresentationProps;
    mainSection?: InjectableCss;
    warningIcon?: IconPresentationProps;
    warningSection?: InjectableCss;
    warningText?: InjectableCss;
}

export const cartLinesStyles: CartLinesStyles = {};

const StyledSection = getStyledWrapper("section");

const styles = cartLinesStyles;

const CartLines: FC<Props> = ({
    cart,
    promotionsDataView,
    settingsCollection,
    clearCurrentCart,
    fields,
    isClearingCart,
    isUpdatingCartLine,
    isLoading,
    updateCartLine,
    removeCartLine,
    isRemovingCartLine,
    showPricing,
    showInventory,
    // XNG-CHANGES
    isTabletScreen,
    isCheckoutReviewAndSubmitPage,
    isCartPage,
    isOrderConfirmationPage,
    isPreloadingData,
}) => {
    const [isCondensed, setIsCondensed] = useState(false);

    let groups: Group[] = [];
    if (cart?.properties?.shipmentGroupingInfo) {
        groups = JSON.parse(cart?.properties?.shipmentGroupingInfo)?.groups;
    }

    // XNG-Changes: adding spinner when opening cart from quick order page
    if (!cart || (cart.lineCount && !cart.cartLines) || isClearingCart || (!cart.lineCount && isLoading[cart.id])) {
        return (
            <StyledSection {...styles.loadingSpinnerCenteringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledSection>
        );
    }

    const cartLines = cart.cartLines || [];

    if (cartLines.length === 0) {
        return (
            <StyledSection {...styles.centeringWrapper}>
                <Icon {...styles.noCartLinesIcon} />
                <Typography {...styles.noCartLinesText} data-test-selector="cart_noCartLinesMessage">
                    {siteMessage("Cart_NoOrderLines")}
                </Typography>
            </StyledSection>
        );
    }

    const isCondensedChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsCondensed(value);
    };
    const removeAllClickHandler = () => {
        clearCurrentCart();
    };

    const { productSettings } = settingsCollection;
    const { value: promotions } = promotionsDataView;
    const productsCannotBePurchased = cartLines.some(o => o.isRestricted || !o.isActive || o.cantBuy);
    const isLoadingCart =
        isLoading[cart.id] || (cart.status === "AlternateCart" && isLoading[API_URL_CURRENT_FRAGMENT]);

    const cartLinesDisplay = (cartLines: CartLineModel[], showGroups: boolean, showEligibleIcon: boolean) => {
        return cartLines.map(cartLine => {
            const matchingPromotions = promotions ? promotions.filter(promo => promo.orderLineId === cartLine.id) : [];
            const showRemoveAction = !cartLine.isPromotionItem && cart.canModifyOrder;
            const CartLineExpanded =
                isCheckoutReviewAndSubmitPage || isOrderConfirmationPage
                    ? CartLineCardExpandedComponent
                    : CartLineCardExpanded;

            const editable = isCartPage || isCheckoutReviewAndSubmitPage;
            return (
                <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                    {isCondensed ? (
                        <CartLineCardCondensed
                            cart={cart}
                            promotions={matchingPromotions}
                            productSettings={productSettings}
                            showInventoryAvailability={true}
                            extendedStyles={styles.cardCondensed}
                            updateCartLine={updateCartLine}
                            removeCartLine={removeCartLine}
                            showRemoveAction={showRemoveAction}
                            showVmiBinDetails={fields.showVmiBinDetails}
                        />
                    ) : (
                        <CartLineExpanded
                            cart={cart}
                            promotions={matchingPromotions}
                            productSettings={productSettings}
                            showInventoryAvailability={true}
                            showLineNotes={fields.showLineNotes}
                            extendedStyles={styles.cardExpanded}
                            updateCartLine={updateCartLine}
                            removeCartLine={removeCartLine}
                            showRemoveAction={showRemoveAction}
                            showVmiBinDetails={fields.showVmiBinDetails}
                            showAddToList={fields.showAddToList}
                            showBrand={fields.showBrand}
                            showImage={fields.showImage}
                            showPerUnitPrice={fields.showPerUnitPrice}
                            showProductNumber={fields.showProductNumber}
                            showProductTitle={fields.showProductTitle}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            editable={editable}
                            {...{ showGroups, showEligibleIcon }}
                        />
                    )}
                </CartLineContext.Provider>
            );
        });
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledSection {...styles.wrapper}>
                <LoadingOverlay
                    {...styles.loadingOverlay}
                    // XNG-CHANGES : remove promotionsDataView.value (sometimes cart keeps loading)
                    loading={isLoadingCart || isUpdatingCartLine || isRemovingCartLine || isPreloadingData}
                >
                    <CartLinesHeader
                        totalItemCount={cartLines.length}
                        isCondensed={isCondensed}
                        productsCannotBePurchased={productsCannotBePurchased}
                        cartNotPriced={cart.cartNotPriced}
                        hasInsufficientInventory={cart.hasInsufficientInventory && cart.fulfillmentMethod === "Ship"}
                        hasInvalidPrice={hasProductsWithInvalidPrice(cart)}
                        failedToGetRealTimeInventory={cart.failedToGetRealTimeInventory}
                        onIsCondensedChange={isCondensedChangeHandler}
                        onRemoveAllClick={removeAllClickHandler}
                        shipmentGroupingInfo={cart?.properties?.shipmentGroupingInfo || ""}
                    />

                    {groups.length ? (
                        <CartLinesGroups
                            cartLines={cartLines}
                            cartLinesDisplay={cartLinesDisplay}
                            currencySymbol={cart.currencySymbol}
                            groups={groups}
                            isCartPage={isCartPage}
                            isTabletScreen={isTabletScreen}
                        />
                    ) : (
                        <>{cartLinesDisplay(cartLines, false, false)}</>
                    )}
                    {/* <CartLinesFooter /> */}
                </LoadingOverlay>
            </StyledSection>
        </StyledWrapper>
    );
};

interface CartLinesHeaderProps {
    totalItemCount: number;
    isCondensed: boolean;
    productsCannotBePurchased?: boolean;
    cartNotPriced?: boolean;
    hasInsufficientInventory?: boolean;
    hasInvalidPrice?: boolean;
    failedToGetRealTimeInventory?: boolean;
    onIsCondensedChange: CheckboxProps["onChange"];
    onRemoveAllClick: () => void;
    showItemCount?: boolean;
    showRemoveAllActions?: boolean;
    shipmentGroupingInfo: string;
}

const CartLinesHeader: FC<CartLinesHeaderProps> = ({
    totalItemCount,
    isCondensed,
    productsCannotBePurchased,
    cartNotPriced,
    hasInsufficientInventory,
    hasInvalidPrice,
    failedToGetRealTimeInventory,
    onIsCondensedChange,
    onRemoveAllClick,
    showItemCount = true,
    showRemoveAllActions = true,
    // XNG-CHANGES
    shipmentGroupingInfo,
}) => {
    return (
        <>
            {/* <StyledSection {...styles.headerWrapper} id="cartHeader">
                <StyledSection {...headerStyles.mainSection}>
                    {showItemCount && (
                        <Typography {...headerStyles.itemCountText}>{`${totalItemCount} ${translate(
                            "Items",
                        )}`}</Typography>
                    )}
                    <CheckboxGroup {...headerStyles.condensedViewCheckboxGroup}>
                        <Checkbox
                            checked={isCondensed}
                            onChange={onIsCondensedChange}
                            data-test-selector="cartlineHeader_condensedCheckbox"
                            {...headerStyles.condensedViewCheckbox}
                        >
                            {translate("Condensed View")}
                            <VisuallyHidden>{translate("for product list")}</VisuallyHidden>
                        </Checkbox>
                    </CheckboxGroup>
                    {showRemoveAllActions && (
                        <Hidden above="md">
                            <OverflowMenu position="end" {...headerStyles.overflowMenu}>
                                <Clickable onClick={onRemoveAllClick}>{translate("Remove All")}</Clickable>
                            </OverflowMenu>
                        </Hidden>
                    )}
                    {showRemoveAllActions && (
                        <Hidden below="lg">
                            <Link
                                onClick={onRemoveAllClick}
                                {...headerStyles.removeAllLink}
                                data-test-selector="cartlineHeader_removeAll"
                            >
                                {translate("Remove All")}
                            </Link>
                        </Hidden>
                    )}
                </StyledSection>
            </StyledSection> */}
            <CustomCartLinesHeader shipmentGroupingInfo={shipmentGroupingInfo} />
        </>
    );
};

const CartLinesFooter: FC = () => {
    const backToTopClickHandler = () => {
        const header = document.getElementById("cartHeader");
        if (header) {
            window.scrollTo(0, header.offsetTop);
        }
    };

    return (
        <StyledSection {...styles.footerWrapper}>
            <Button onClick={backToTopClickHandler} {...styles.backToTopButton} aria-label="Back to Top">
                {translate("Back to Top")}
            </Button>
        </StyledSection>
    );
};

interface CartLinesGroupsProps {
    isCartPage: boolean;
    groups: Group[];
    cartLines: CartLineModel[];
    currencySymbol: string;
    cartLinesDisplay: any;
    isTabletScreen?: boolean;
}

const CartLinesGroups: FC<CartLinesGroupsProps> = ({
    isCartPage,
    groups,
    cartLines,
    currencySymbol,
    cartLinesDisplay,
    isTabletScreen,
}) => {
    const [flatGroups, setFlatGroups] = useState<Group[]>([]);
    const [topVlaues, setTopVlaues] = useState<{ [key: string]: number }>({});
    const subGroupsLengths = groups.reduce((acc, group) => {
        acc += group.subGroups?.length ?? 0;
        return acc;
    }, 0);

    const groupsRef = React.useRef(
        Array.from({ length: subGroupsLengths + groups.length }).map(() => React.createRef<HTMLDivElement>()),
    );

    useEffect(() => {
        setFlatGroups(getFlatGroups(groups, cartLines));
    }, [groups]);

    useEffect(() => {
        const tempTopValues = topVlaues;
        groupsRef.current.forEach((groupRef, index) => {
            tempTopValues[index] = getTopValue(index);
        });
        setTopVlaues(tempTopValues);
    }, [groupsRef.current[0]?.current]);

    const getTopValue = (id: number) => {
        if (id === 0) {
            return 0;
        }
        const groupHeight = isTabletScreen ? 96 : 49;
        const subGroupHeight = isTabletScreen ? 81 : 32;
        const flatGroups = getFlatGroups(groups, cartLines);
        return groupsRef.current.slice(0, id).reduce((acc, groupRef, id) => {
            acc += flatGroups[id]?.type === "group" ? groupHeight : subGroupHeight;
            return acc - 1;
        }, 0);
    };

    // XNG-CHANGES
    const handleScrollClick = (id: number) => {
        const element = document.getElementById(`cartline-${id}`);
        if (element) {
            const rect = element.getBoundingClientRect();
            const scrollTop = window.scrollY;
            const targetScrollTop = scrollTop + rect.top - (id * 90 + 200);

            window.scrollTo({
                top: targetScrollTop,
                behavior: "smooth",
            });
        }
    };
    return (
        <StyledWrapper {...styles.groupsWrapper} {...{ isCartPage }}>
            {flatGroups.map((group, id) => {
                if (group.type === "group") {
                    return (
                        <XngSticky topValue={topVlaues[id] ?? 0} key={`group-${id}`} id={`group-${id}`}>
                            <StyledWrapper
                                {...styles.groupWrapper}
                                ref={groupsRef.current[id]}
                                onClick={() => handleScrollClick(id + 1)}
                            >
                                <LinesHeader extendedStyles={styles.groupsLinesHeaderStyles} hideOnTablet={false}>
                                    <LinesHeader.ItemDescription>
                                        <StyledWrapper {...styles.itemDescriptionWapper} className="shipping-wrapper">
                                            <Typography>{group.title}</Typography>
                                            {group.groupType !== 3 && (
                                                <Typography {...styles.shippingHandlingTypography}>
                                                    {siteMessage(
                                                        "Guest_view_shipping_handling_terms_Msg",
                                                        "view shipping & handling terms",
                                                    )}
                                                </Typography>
                                            )}
                                        </StyledWrapper>
                                    </LinesHeader.ItemDescription>
                                    <LinesHeader.PriceUnit>
                                        <StyledWrapper {...styles.itemDescriptionWapper}>
                                            <Typography weight={400}>{translate("Count")}</Typography>
                                            <Typography>{group.count}</Typography>
                                        </StyledWrapper>
                                    </LinesHeader.PriceUnit>
                                    <LinesHeader.Qty>
                                        <StyledWrapper {...styles.itemDescriptionWapper}>
                                            <Typography weight={400}>{translate("SubTotal")}</Typography>
                                            <LocalizedCurrency
                                                amount={Number(group?.lineSubtotal?.toFixed(2))}
                                                currencySymbol={currencySymbol}
                                            />
                                        </StyledWrapper>
                                    </LinesHeader.Qty>
                                    <LinesHeader.Total>
                                        <StyledWrapper {...styles.itemDescriptionWapper}>
                                            <Typography weight={400}>{translate("Freight Cost")}</Typography>
                                            <Typography>{group.shippingCostDisplay}</Typography>
                                        </StyledWrapper>
                                    </LinesHeader.Total>
                                </LinesHeader>
                            </StyledWrapper>
                        </XngSticky>
                    );
                }
                const subGroup = group as SubGroups;
                return (
                    <>
                        <XngSticky topValue={topVlaues[id] ?? 0} key={`sub-group-${id}`} id={`sub-group-${id}`}>
                            <StyledWrapper
                                {...styles.groupWrapper}
                                ref={groupsRef.current[id]}
                                onClick={() => handleScrollClick(id)}
                            >
                                <LinesHeader
                                    hideOnTablet={false}
                                    extendedStyles={styles.subGroupsLinesHeaderStyles}
                                    className={`${flatGroups[id - 1]?.type === "group" ? "first-sub-group" : ""}`}
                                >
                                    <LinesHeader.ItemDescription>{subGroup.title}</LinesHeader.ItemDescription>
                                    <LinesHeader.PriceUnit>
                                        <StyledWrapper {...styles.itemDescriptionWapper}>
                                            <Typography {...styles.linesHeaderLabelTypography}>
                                                {translate("Count")}
                                            </Typography>
                                            <Typography>{subGroup.lineItems?.length}</Typography>
                                        </StyledWrapper>
                                    </LinesHeader.PriceUnit>
                                    <LinesHeader.Qty>
                                        <StyledWrapper {...styles.itemDescriptionWapper}>
                                            <Typography {...styles.linesHeaderLabelTypography}>
                                                {translate("SubTotal")}
                                            </Typography>
                                            <LocalizedCurrency
                                                amount={Number(subGroup?.lineSubtotal?.toFixed(2))}
                                                currencySymbol={currencySymbol}
                                            />
                                        </StyledWrapper>
                                    </LinesHeader.Qty>
                                    <LinesHeader.Total>
                                        {subGroup.shippingCostDisplay && (
                                            <StyledWrapper {...styles.itemDescriptionWapper}>
                                                <Typography {...styles.linesHeaderLabelTypography}>
                                                    {translate("Freight Cost")}
                                                </Typography>
                                                <Typography>{subGroup.shippingCostDisplay}</Typography>
                                            </StyledWrapper>
                                        )}
                                    </LinesHeader.Total>
                                </LinesHeader>
                            </StyledWrapper>
                        </XngSticky>
                        <StyledWrapper id={`cartline-${id}`}>
                            {subGroup?.cartLines &&
                                cartLinesDisplay(subGroup?.cartLines, true, subGroup.showEligibleIcon)}
                        </StyledWrapper>
                    </>
                );
            })}
        </StyledWrapper>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartLines),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage", "CheckoutReviewAndSubmitPage", "OrderConfirmationPage"],
        fieldDefinitions: [
            {
                name: fields.showLineNotes,
                displayName: "Show Line Notes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                tooltip: "This setting does not apply to Punchout orders, which will never display Line Notes.",
                tab: basicTab,
                sortOrder: 0,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 1,
            },
            {
                name: fields.showProductTitle,
                displayName: "Product title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 2,
            },
            {
                name: fields.showBrand,
                displayName: "Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 3,
            },
            {
                name: fields.showRemoveAllActions,
                displayName: "Remove all actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 4,
            },
            {
                name: fields.showPerUnitPrice,
                displayName: "Per unit price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 5,
            },
            {
                name: fields.showPriceLabel,
                displayName: "Price label",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 6,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to list",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 7,
            },
            {
                name: fields.showItemCount,
                displayName: "Item count",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 8,
            },
            {
                name: fields.showProductNumber,
                displayName: "Product number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 9,
            },
            {
                name: fields.showBackToTop,
                displayName: "Back to top",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 10,
            },
            {
                name: fields.showVmiBinDetails,
                displayName: "VMI Bin details",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isEnabled: () => true,
                fieldType: "General",
                tab: basicTab,
                sortOrder: 11,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
